import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { deleteNews, fetchNewsData, updateNewsData } from "../../redux/actions/GalleryAndNews";
import { format } from "date-fns";
import { MdOutlineDeleteForever } from "react-icons/md";
import { FaRegEdit } from "react-icons/fa";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import PageComponent from "../../Components/PageComponent";
import Page from "../../Components/Page";
import Sidebar from "../../Components/Sidebar";

function NewsList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: newsList = [], loading, error } = useSelector(
    (state) => state.GalleryAndNews.News
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedNews, setSelectedNews] = useState({
    title: "",
    date: "",
    description: "",
    images: [],
  });

  useEffect(() => {
    dispatch(fetchNewsData());
  }, [dispatch]);

  const handleEdit = (news) => {
    setSelectedNews({
      ...news,
      description: news.description || "", // Ensure description is always a string
      images: news.images || [], // Ensure images is always an array
      date: news.date ? format(new Date(news.date), "yyyy-MM-dd") : "", // Format date for input field
    });
    setIsModalOpen(true);
  };

  const handleDelete = (news) => {
    setSelectedNews(news); // Set the selected news to delete
    setIsDeleteModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedNews(null);
  };

  const handleDeleteCloseModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedNews(null);
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();
  
    // Basic validation
    if (
      !selectedNews.title.trim() ||
      !selectedNews.date ||
      !selectedNews.description.trim()
    ) {
      alert("Title, Date, and Description are required.");
      return;
    }
  
    // Extract filename from image URLs, and handle new files
    const existingImages = selectedNews.images
      .filter((image) => typeof image === "string" && image) // Filter out empty strings and files
      .map((image) => {
        const parts = image.split("/");
        return parts[parts.length - 1].split("?")[0]; // Extract filename before query params
      });
  
    // Prepare FormData for the API
    const formData = new FormData();
    formData.append("title", selectedNews.title);
    formData.append("description", selectedNews.description);
    formData.append("date", selectedNews.date);
    formData.append("existing_images", JSON.stringify(existingImages)); // Send existing image filenames
  
    // Append new image files to the form data
    selectedNews.images.forEach((image, index) => {
      if (typeof image !== "string" && image instanceof File) {
        formData.append(`file`, image); // Append new files with the key 'images'
      }
    });
  
    try {
      // Dispatch the update action (assuming updateNewsData handles the API call)
      await dispatch(updateNewsData(selectedNews.id, formData));
  
      // Fetch updated news list
      await dispatch(fetchNewsData());
  
      // Close modal
      handleCloseModal();
  
      // Success message
      alert("News updated successfully!");
    } catch (error) {
      console.error("Error updating news:", error);
      alert("Failed to update news. Please try again.");
    }
  };
  

  const confirmDelete = () => {
    if (selectedNews) {
      dispatch(deleteNews(selectedNews.id));
      handleDeleteCloseModal();
    }
  };

  if (loading) {
    return <div className="text-center text-gray-700">Loading...</div>;
  }

  if (error) {
    return (
      <div className="text-center text-red-600">
        Error: {error || "Failed to load news"}
      </div>
    );
  }

  return (
     <>
                       <Page className="px-10 py-6">
    <div className="flex flex-col p-6 bg-[#F8F7FA] h-screen">
       <div className="fixed top-0 left-0 w-64 h-full overflow-y-auto bg-white shadow-md">
                              <Sidebar />
                            </div>
      <div className="mb-6">
      <div className="flex items-center space-x-2 text-3xl text-gray-500">
          <span>Gallery & News</span>
          <span>/</span>
          <span className="text-black">News</span>
        </div>
        <div className="flex items-center mt-4 space-x-4">
          <button
            className="px-4 py-2 text-[#92288C] border border-[#92288C] rounded-md shadow hover:bg-gray-100"
            onClick={() => navigate("/gallery&News/add-news")}
          >
            Add New News
          </button>
          <button
            className="px-4 py-2 text-white bg-[#92288C] rounded-md shadow hover:bg-[#6C0B67]">
            List of News
          </button>
        </div>
      </div>

      {/* News Table */}
      <div className="overflow-hidden bg-white rounded-lg shadow-md">
        <table className="w-full border-collapse table-auto">
          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="px-6 py-4 font-medium text-left text-gray-700">Title</th>
              <th className="px-6 py-4 font-medium text-left text-gray-700">Date</th>
              <th className="px-6 py-4 font-medium text-left text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {newsList.length > 0 ? (
              newsList.map((news, index) => (
                <tr
                  key={news.id}
                  className={`border-b ${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                >
                  <td className="px-6 py-4 text-gray-800">{news.title}</td>
                  <td className="px-6 py-4 text-gray-600">
                    {format(new Date(news.date), "yyyy-MM-dd")}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-4">
                      <button
                        type="button"
                        className="flex items-center px-5 py-1 text-gray-400 bg-gray-100 border border-gray-400 rounded"
                        onClick={() => handleDelete(news)}
                      >
                        Delete
                        <MdOutlineDeleteForever className="w-5 h-5 ml-2" />
                      </button>
                      <button
                        type="button"
                        className="flex items-center px-5 py-1 border rounded text-primary-color border-primary-color bg-primary-color bg-opacity-10"
                        onClick={() => handleEdit(news)}
                      >
                        Edit
                        <FaRegEdit className="w-4 h-4 ml-2" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="3"
                  className="px-6 py-4 font-medium text-center text-gray-600"
                >
                  No news available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-1/2 p-6 bg-white rounded-lg shadow-lg">
            <h2 className="mb-4 text-xl font-bold">Edit News</h2>
            <form onSubmit={handleSaveChanges}>
              <div className="space-y-4">
                {/* Title and Date */}
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <label className="block text-gray-700">Title</label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                      value={selectedNews.title}
                      onChange={(e) =>
                        setSelectedNews({ ...selectedNews, title: e.target.value })
                      }
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-gray-700">Date</label>
                    <input
                      type="date"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                      value={selectedNews.date}
                      onChange={(e) =>
                        setSelectedNews({ ...selectedNews, date: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* Description */}

                <div className="mb-2">
                  <label className="block text-gray-700">Description</label>
                  <div className="mt-2">
                    {/* Added margin to avoid overlap */}
                    <ReactQuill
                      value={selectedNews.description || ""}
                      onChange={(value) =>
                        setSelectedNews({
                          ...selectedNews,
                          description: value,
                        })
                      }
                      className="bg-white border border-gray-300 rounded-md"
                      placeholder="Enter description"
                      style={{ height: "300px" }} // Adjusted height for a better editing experience
                      modules={{
                        toolbar: [
                          [{ header: [1, 2, 3, false] }], // Heading styles
                          [{ font: [] }], // Font selection
                          ["bold", "italic", "underline"], // Font styles
                          [{ align: [] }], // Alignment options
                          ["clean"], // Clear formatting
                        ],
                      }}
                      formats={[
                        "font",
                        "header",
                        "bold",
                        "italic",
                        "underline",
                        "align",
                      ]}
                      theme="snow"
                    />
                  </div>
                </div>
              </div>
              {/* Images */}
              <div>
                <label className="block mb-2 text-gray-700">Upload Image</label>
                {selectedNews.images.map((image, index) => (
                  <div key={index} className="flex items-center mb-2 space-x-4">
                    {image ? (
                      // Display the image name if an image exists
                      <span className="flex-1 px-4 py-2 truncate bg-gray-100 border border-gray-300 rounded-md">
                        {typeof image === "string"
                          ? image.split("/").pop()
                          : image.name}
                      </span>
                    ) : (
                      // Show file input for uploading a new image
                      <input
                        type="file"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const updatedImages = [...selectedNews.images];
                            updatedImages[index] = file; // Replace the current empty field with the selected file
                            setSelectedNews({
                              ...selectedNews,
                              images: updatedImages,
                            });
                          }
                        }}
                      />
                    )}

                    {/* Remove Button */}
                    <button
                      type="button"
                      onClick={() => {
                        const updatedImages = [...selectedNews.images];
                        updatedImages.splice(index, 1); // Remove the image at the current index
                        setSelectedNews({
                          ...selectedNews,
                          images: updatedImages,
                        });
                      }}
                      className="px-4 py-2 text-red-600 border border-red-600 rounded-md hover:bg-red-100"
                    >
                      Remove
                    </button>
                  </div>
                ))}

                {/* Add More Button */}
                <button
                  type="button"
                  onClick={() =>
                    setSelectedNews({
                      ...selectedNews,
                      images: [...selectedNews.images, null], // Add a `null` value to display a "Browse Image" input
                    })
                  }
                  className="mt-2 text-sm text-[#92288C] hover:underline flex items-center"
                >
                  <span className="mr-1">+</span> Add more
                </button>
              </div>

              <div className="flex justify-end mt-6 space-x-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-white bg-[#92288C] rounded-md hover:bg-[#6C0B67]"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-[400px] text-center">
            <h3 className="mb-4 text-xl font-semibold text-gray-700">Delete?</h3>
            <p className="mb-6 text-gray-600">
              Are you sure you want to delete this news?
              <br />
              <strong>{selectedNews?.title}</strong>
            </p>
            <div className="flex justify-center gap-4">
              <button
                className="px-6 py-2 text-gray-500 bg-gray-200 rounded-md hover:bg-gray-300"
                onClick={handleDeleteCloseModal}
              >
                No
              </button>
              <button
                className="px-6 py-2 text-white bg-[#92288C] rounded-md"
                onClick={confirmDelete}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </Page>
    </>
  );
}

export default NewsList;
